import { Input } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

const { TextArea } = Input;

const LineNumberedTextArea = ({
  value = '',
  onChange,
  onKeyDown,
  onLineErrors,
  placeholder = '',
  maxLength = 5000,
  maxLengthPerLine = 250,
  rows = 8,
  showCount = true,
  className = '',
  disabled = false,
  ...restProps
}) => {
  const textAreaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const measureHelperRef = useRef(null);

  // Derive logical lines and line errors directly from value
  const { lines, lineErrors } = useMemo(() => {
    const splitLines = value ? value.split('\n') : [''];
    const errors = [];

    splitLines.forEach((line, index) => {
      if (line.length > maxLengthPerLine) {
        errors.push(index);
      }
    });

    return { lines: splitLines, lineErrors: errors };
  }, [value, maxLengthPerLine]);

  // Notify parent component about line errors whenever they change
  useEffect(() => {
    onLineErrors?.(lineErrors);
  }, [lineErrors, onLineErrors]);

  // Sync line-number div heights to match the visual (wrapped) height of each
  // logical line in the textarea. Uses a hidden mirror div with identical font
  // and width settings to measure each line's rendered height.
  const syncLineHeights = useCallback(() => {
    const wrapper = lineNumbersRef.current?.parentElement;
    const nativeTextarea = wrapper?.querySelector('textarea');
    const helper = measureHelperRef.current;
    const lineNumberEls =
      lineNumbersRef.current?.querySelectorAll('.line-number');

    if (!nativeTextarea || !helper || !lineNumberEls) return;

    // Mirror the textarea's computed text styles into the helper div
    // eslint-disable-next-line no-undef
    const cs = window.getComputedStyle(nativeTextarea);
    helper.style.width = `${nativeTextarea.clientWidth}px`;
    helper.style.font = cs.font;
    helper.style.fontSize = cs.fontSize;
    helper.style.fontFamily = cs.fontFamily;
    helper.style.lineHeight = cs.lineHeight;
    helper.style.letterSpacing = cs.letterSpacing;
    helper.style.padding = cs.padding;
    helper.style.paddingTop = '0';
    helper.style.paddingBottom = '0';
    helper.style.boxSizing = 'content-box';
    helper.style.whiteSpace = 'pre-wrap';
    helper.style.wordWrap = 'break-word';
    helper.style.overflowWrap = 'break-word';

    const splitLines = value ? value.split('\n') : [''];

    splitLines.forEach((line, index) => {
      const el = lineNumberEls[index];
      if (!el) return;

      // Use a non-breaking space for empty lines so the helper div has height
      helper.textContent = line || '\u00a0';
      const measuredHeight = helper.scrollHeight;
      el.style.height = `${measuredHeight}px`;
    });
  }, [value]);

  // Attach scroll and resize listeners directly to the native textarea element
  // Ant Design's onScroll fires on the wrapper, not the native textarea
  useEffect(() => {
    // Find the native textarea via DOM traversal — more reliable than the
    // Ant Design imperative-handle chain which may not be ready at mount time
    const wrapper = lineNumbersRef.current?.parentElement;
    const nativeTextarea = wrapper?.querySelector('textarea');
    if (!nativeTextarea) return undefined;

    const syncScroll = () => {
      if (lineNumbersRef.current) {
        const maxTextareaScroll =
          nativeTextarea.scrollHeight - nativeTextarea.clientHeight;
        const maxLineNumbersScroll =
          lineNumbersRef.current.scrollHeight -
          lineNumbersRef.current.clientHeight;

        if (maxTextareaScroll > 0 && maxLineNumbersScroll > 0) {
          const scrollRatio = nativeTextarea.scrollTop / maxTextareaScroll;
          lineNumbersRef.current.scrollTop = scrollRatio * maxLineNumbersScroll;
        } else {
          lineNumbersRef.current.scrollTop = nativeTextarea.scrollTop;
        }
      }
    };

    nativeTextarea.addEventListener('scroll', syncScroll);

    // eslint-disable-next-line no-undef
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? // eslint-disable-next-line no-undef
          new ResizeObserver(() => {
            syncScroll();
            syncLineHeights();
          })
        : null;

    resizeObserver?.observe(nativeTextarea);

    return () => {
      nativeTextarea.removeEventListener('scroll', syncScroll);
      resizeObserver?.disconnect();
    };
  }, [syncLineHeights]);

  // Re-sync line heights whenever value changes
  useEffect(() => {
    syncLineHeights();
  }, [syncLineHeights]);

  // Handle text change
  const handleChange = useCallback(
    (e) => {
      onChange?.(e);
    },
    [onChange],
  );

  // Handle key down - allowing Enter key for line breaks
  const handleKeyDown = useCallback(
    (e) => {
      onKeyDown?.(e);
    },
    [onKeyDown],
  );

  // Generate line number divs from logical lines
  const generateLineNumbers = useMemo(() => {
    return lines.map((line, index) => {
      const lineNumber = index + 1;
      const hasError = lineErrors.includes(index);
      let lineClassName = 'line-number';
      if (hasError) lineClassName += ' line-error';
      if (line === '') lineClassName += ' empty-line';

      return (
        <div
          key={`line-${lineNumber}`}
          className={lineClassName}
          data-line={lineNumber}
          data-logical-index={index}
        >
          {lineNumber}
        </div>
      );
    });
  }, [lines, lineErrors]);

  return (
    <div
      className={`line-numbered-textarea-container ${className} ${disabled ? 'disabled' : ''}`}
      role="textbox"
      aria-label="Line numbered text area"
    >
      <div
        ref={measureHelperRef}
        className="line-measure-helper"
        aria-hidden="true"
      />
      <div className="line-numbered-textarea-wrapper">
        <div ref={lineNumbersRef} className="line-numbers" aria-hidden="true">
          {generateLineNumbers}
        </div>
        <TextArea
          ref={textAreaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          showCount={showCount}
          className="line-numbered-textarea"
          disabled={disabled}
          {...restProps}
        />
      </div>
      {lineErrors.length > 0 && (
        <div className="line-errors-warning">
          <span className="text-danger">
            Warning: Line{lineErrors.length > 1 ? 's' : ''}{' '}
            {lineErrors.map((i) => i + 1).join(', ')} exceed
            {lineErrors.length === 1 ? 's' : ''} {maxLengthPerLine} characters
          </span>
        </div>
      )}
    </div>
  );
};

export default LineNumberedTextArea;
